import { StCeloManagerAbi } from '../abis/stcelo'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * StakedCelo (stCELO) reader.
 *
 * Two calls: `stCELO.totalSupply()` + `Manager.toCelo(1e18)` (returns
 * the 1e18-scaled CELO-per-stCELO rate). stCELO is value-accruing
 * (non-rebasing); the Manager holds the rate, not the token.
 *
 * The Manager address is pinned in
 * `entry.readerExtras.stceloManager`. Falls back to a 1:1 placeholder
 * if absent.
 */
export const readerStCelo = (entry: LstRegistryEntry): LstReader => {
  const manager = entry.readerExtras?.stceloManager
  if (!manager) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: manager, name: 'toCelo', params: [ONE_E18] },
    ],
    abis: [TotalSupplyAbi, StCeloManagerAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
      }
    },
  }
}
